/**
 * Service 抽象层（4 个模块全部接入真实接口；mock 已彻底移除）
 *
 * 接口契约：
 * - getKpiOverview → POST /api/v1/marketing/ad_creative_multi/overview
 * - getTimeline    → POST /api/v1/marketing/ad_creative_multi/timeline
 * - getSum         → POST /api/v1/marketing/ad_creative_multi/sum
 * - getStats       → POST /api/v1/marketing/ad_creative_multi/stats
 *
 * ⚠️ 签名约定（与 marketing 模块一致，兼容 `wrapperCancelRequest`）：
 *   service 函数接收 `payload: { data, cancelToken?, headers? }`
 *   `http({ url, method, ...payload })` 自动展开
 */
import {
  IKpiOverviewParams,
  IKpiOverviewResponse,
} from '../types/overview';
import {
  ITimelineRawParams,
  ITimelineRawResponse,
} from '../types/trend';
import {
  IStatsRawParams,
  IStatsRawResponse,
} from '../types/country';
import { ISumParams, ISumResponse } from '../types/sum';
import {
  IDimensionSummaryParams,
  IDimensionSummaryResponse,
} from '../types/dimensionSummary';

import http from '@/pages/marketing/services/utils';
import { prefix } from '@/pages/marketing/services/constants';

/** 通用 payload 类型 */
interface IRequestPayload<T> {
  data: T;
  cancelToken?: any;
  headers?: any;
}

/**
 * 投放概览（Overview）
 * 接口文档：specs/多游戏投放对比页/api/overview.md
 */
export function getKpiOverview(
  payload: IRequestPayload<IKpiOverviewParams>,
): Promise<IKpiOverviewResponse> {
  return http({
    url: `${prefix}/ad_creative_multi/overview`,
    method: 'post',
    ...payload,
  }) as Promise<IKpiOverviewResponse>;
}

/**
 * 通用 timeline 接口（trend / channel-line / channel-bar / channel-pie 共用）
 * 接口文档：specs/多游戏投放对比页/api/timeline.md
 *
 * 返回三层结构（times[] + games[].timeline[].values[].dimension_value）的 raw 数据，
 * 由各模块 hook 自行做 raw → 友好结构 的转换。
 */
export function getTimeline(
  payload: IRequestPayload<ITimelineRawParams>,
): Promise<ITimelineRawResponse> {
  return http({
    url: `${prefix}/ad_creative_multi/timeline`,
    method: 'post',
    ...payload,
  }) as Promise<ITimelineRawResponse>;
}

/**
 * 通用 sum 接口（区间汇总，一次拉多 metric）
 * 接口文档：specs/多游戏投放对比页/api/sum.md
 * 主要供 ChannelCompare 排名表使用：5 个 metric（in active ads / channel share /
 * competitor share / score / impressions）一次拉全，避免 metric Segment 切换时重复请求。
 */
export function getSum(
  payload: IRequestPayload<ISumParams>,
): Promise<ISumResponse> {
  return http({
    url: `${prefix}/ad_creative_multi/sum`,
    method: 'post',
    ...payload,
  }) as Promise<ISumResponse>;
}

/**
 * 通用 stats 接口（按维度展开 game_id × dimension_value）
 * 接口文档：specs/多游戏投放对比页/api/stats.md
 * 主要供 CountryCompare 使用：
 * - group_by=dimension_value：分国家游戏对比矩阵（左侧表格：国家为行、游戏为列）
 * - group_by=game_id：单游戏多国家详情（右侧详情面板）
 */
export function getStats(
  payload: IRequestPayload<IStatsRawParams>,
): Promise<IStatsRawResponse> {
  return http({
    url: `${prefix}/ad_creative_multi/stats`,
    method: 'post',
    ...payload,
  }) as Promise<IStatsRawResponse>;
}

/**
 * 维度汇总接口（区间日均，按 dimension_value 展开 + 可选环比）
 * 接口文档：specs/多游戏投放对比页/api/summary.md
 *
 * 适用场景：
 * - Module A 饼图（pie）：每个游戏在区间内的指标聚合值
 * - Module B 柱状图 / 矩阵表（bar / matrix）：每个 (game, channel) 的区间日均 + 环比
 *
 * 与 timeline 的差异：不做时间扩窗、支持环比、返回单值列表（无 timeline 数组）。
 */
export function getDimensionSummary(
  payload: IRequestPayload<IDimensionSummaryParams>,
): Promise<IDimensionSummaryResponse> {
  return http({
    url: `${prefix}/ad_creative_multi/dimension_summary`,
    method: 'post',
    ...payload,
  }) as Promise<IDimensionSummaryResponse>;
}
